import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import {
    CharacterName,
    CharacterProgressionSummary,
    CharacterSummary,
    LightLinkOutSvg,
    ThemedBackdropSvg,
    ThemedBuilderSvg,
    ThemedExportSvg,
    ThemedFrameSvg,
    ThemedLongRestSvg,
    ThemedManageLevelSvg,
    ThemedManageXpSvg,
    ThemedPaintBrushSvg,
    ThemedPortraitSvg,
    ThemedPreferencesSvg,
    ThemedShareSvg,
    ThemedShortRestSvg,
    ThemedThemeIconSvg,
    ThemedChatBubbleSvg,
    CharacterAvatarPortrait,
    LightPencilSvg,
} from '@dndbeyond/character-components/es';
import {
    CharacterPreferences,
    CharClass,
    Constants,
    ExperienceInfo,
    HelperUtils,
    Race,
    RuleData,
    rulesEngineSelectors,
    ClassUtils,
    characterActions,
    DecorationInfo,
    DecorationUtils,
} from '@dndbeyond/character-rules-engine/es';

import config from '~/config';

import { sheetActions } from '../../../../CharacterSheet/actions/sheet';
import { sheetAppSelectors } from '../../../../CharacterSheet/selectors';
import { sidebarActions } from '../../../actions/sidebar';
import PaneMenu, { PaneMenuGroup, PaneMenuItem } from '../../../components/Pane/PaneMenu';
import { ThemeLinkButton } from '../../../components/common/LinkButton';
import { PaneHistoryInitTypeEnum, ThemeModeEnum } from '../../../constants/App';
import * as appEnvSelectors from '../../../selectors/appEnv';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, PaneUtils } from '../../../utils';

const BASE_PATHNAME = config.basePathname;

interface Props extends DispatchProp {
    classes: Array<CharClass>;
    name: string;
    defaultCharacterName: string;
    gender: string | null;
    race: Race | null;
    deathCause: Constants.DeathCauseEnum;
    ruleData: RuleData;
    xpInfo: ExperienceInfo;
    isReadonly: boolean;
    preferences: CharacterPreferences;
    builderUrl: string;
    decorationInfo: DecorationInfo;
    userId: number;
    playerName: string;
    playerId: number;
}
interface State {
    isNameEditorVisible: boolean;
}
class CharacterManagePane extends React.PureComponent<Props, State> {
    editorRef = React.createRef<HTMLInputElement>();

    constructor(props: Props) {
        super(props);

        this.state = {
            isNameEditorVisible: false,
        };
    }

    handleDecorateMenuClick = (evt: React.MouseEvent): void => {
        evt.stopPropagation();
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.DECORATE));
    };

    handleXpMenuClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.XP));
    };

    handleGameLogMenuClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.GAME_LOG));
    };

    handleShortRestMenuClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.SHORT_REST));
    };

    handleLongRestMenuClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.LONG_REST));
    };

    handlePreferencesMenuClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.PREFERENCES));
    };

    handleShareMenuClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sheetActions.shareUrl(PaneHistoryInitTypeEnum.PUSH));
    };

    handlePdfClick = (): void => {
        const { dispatch } = this.props;
        dispatch(sheetActions.exportPdf(PaneHistoryInitTypeEnum.PUSH));
    };

    handleBuilderClick = (): void => {
        const { builderUrl } = this.props;

        // @ts-ignore
        window.location = builderUrl;
    };

    getMenuIconNode = (menuKey: string): React.ReactNode => {
        const { decorationInfo } = this.props;

        const menuIconLookup: Record<string, React.ComponentType> = {
            builder: ThemedBuilderSvg,
            shortrest: ThemedShortRestSvg,
            longrest: ThemedLongRestSvg,
            share: ThemedShareSvg,
            managelevel: ThemedManageLevelSvg,
            managexp: ThemedManageXpSvg,
            gamelog: ThemedChatBubbleSvg,
            frame: ThemedFrameSvg,
            backdrop: ThemedBackdropSvg,
            theme: ThemedThemeIconSvg,
            portrait: ThemedPortraitSvg,
            preferences: ThemedPreferencesSvg,
            downloadpdf: ThemedExportSvg,
        };

        let IconNode: React.ComponentType<any> | null = null;

        if (menuIconLookup[menuKey]) {
            IconNode = HelperUtils.lookupDataOrFallback(menuIconLookup, menuKey);
        }

        if (IconNode === null) {
            return null;
        }

        return <IconNode theme={DecorationUtils.getCharacterTheme(decorationInfo)} />;
    };

    renderClassList = (): React.ReactNode => {
        const { classes } = this.props;

        return classes.map((charClass) => {
            const subClassText: string = ClassUtils.getSubclassName(charClass) ?? 'No Subclass';

            return (
                <div className="ct-character-manage-pane__class-list-item" key={ClassUtils.getId(charClass)}>
                    <div
                        className="ct-character-manage-pane__class-list-item-image"
                        style={{
                            backgroundImage: `url(${ClassUtils.getPortraitUrl(charClass)})`,
                        }}
                    >
                        <div className="ct-character-manage-pane__class-list-item-level">
                            {ClassUtils.getLevel(charClass)}
                        </div>
                    </div>
                    <div className="ct-character-manage-pane__class-list-item-summary">
                        <div className="ct-character-manage-pane__class-list-item-label">
                            {ClassUtils.getName(charClass)}
                        </div>
                        <div className="ct-character-manage-pane__class-list-item-meta">{subClassText}</div>
                    </div>
                </div>
            );
        });
    };

    renderMenu = () => {
        const { preferences } = this.props;

        const LightLinkOutIcon: React.ReactNode = <LightLinkOutSvg />;

        return (
            <div className="ct-character-manage-pane__menu">
                <PaneMenu>
                    <PaneMenuGroup label="My Character">
                        <PaneMenuItem
                            menukey="builder"
                            prefixIcon={this.getMenuIconNode('builder')}
                            suffixIcon={LightLinkOutIcon}
                            onClick={this.handleBuilderClick}
                        >
                            Manage Character & Levels
                        </PaneMenuItem>
                        {preferences.progressionType === Constants.PreferenceProgressionTypeEnum.XP && (
                            <PaneMenuItem
                                menukey="managexp"
                                prefixIcon={this.getMenuIconNode('managexp')}
                                onClick={this.handleXpMenuClick}
                            >
                                Manage Experience
                            </PaneMenuItem>
                        )}
                        <PaneMenuItem
                            menukey="preferences"
                            prefixIcon={this.getMenuIconNode('preferences')}
                            onClick={this.handlePreferencesMenuClick}
                        >
                            Character Settings
                        </PaneMenuItem>
                    </PaneMenuGroup>
                    <PaneMenuGroup label="Play">
                        <PaneMenuItem
                            menukey="gamelog"
                            prefixIcon={this.getMenuIconNode('gamelog')}
                            onClick={this.handleGameLogMenuClick}
                        >
                            Game Log
                        </PaneMenuItem>
                        <PaneMenuItem
                            menukey="shortrest"
                            prefixIcon={this.getMenuIconNode('shortrest')}
                            onClick={this.handleShortRestMenuClick}
                        >
                            Short Rest
                        </PaneMenuItem>
                        <PaneMenuItem
                            menukey="longrest"
                            prefixIcon={this.getMenuIconNode('longrest')}
                            onClick={this.handleLongRestMenuClick}
                        >
                            Long Rest
                        </PaneMenuItem>
                    </PaneMenuGroup>
                    <PaneMenuGroup label="Share">
                        {preferences.privacyType === Constants.PreferencePrivacyTypeEnum.PUBLIC && (
                            <PaneMenuItem
                                menukey="share"
                                prefixIcon={this.getMenuIconNode('share')}
                                onClick={this.handleShareMenuClick}
                            >
                                Shareable Link
                            </PaneMenuItem>
                        )}
                        <PaneMenuItem
                            menukey="downloadpdf"
                            prefixIcon={this.getMenuIconNode('downloadpdf')}
                            onClick={this.handlePdfClick}
                            className="character-sheet-download-pdf-button character-tools-export-pdf-button"
                        >
                            Export to PDF
                        </PaneMenuItem>
                    </PaneMenuGroup>
                </PaneMenu>
            </div>
        );
    };

    renderReadonlyCta = () => {
        return (
            <div className="ct-character-manage-pane__readonly">
                <div className="ct-character-manage-pane__readonly-background-image" />
                <div className="ct-character-manage-pane__readonly-image-overlay" />
                <div className="ct-character-manage-pane__readonly-body">
                    <div className="ct-character-manage-pane__readonly-text">
                        You are viewing a read-only Character Sheet
                    </div>
                    <ThemeLinkButton
                        className="ct-character-manage-pane__readonly-cta"
                        size="large"
                        target="_blank"
                        url={`${BASE_PATHNAME}/builder`}
                        block={false}
                    >
                        <span className="ct-character-manage-pane__readonly-cta-text">Create a Character</span>
                        <LightLinkOutSvg />
                    </ThemeLinkButton>
                </div>
            </div>
        );
    };

    handleNameClick = (): void => {
        this.setState(
            {
                isNameEditorVisible: true,
            },
            () => {
                this.editorRef.current && this.editorRef.current.focus();
            },
        );
    };

    handleNameChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
        const { dispatch, name } = this.props;
        const inputValue = evt.target.value;

        if (inputValue !== name && inputValue !== '') {
            dispatch(characterActions.nameSet(inputValue));
        }

        this.setState({
            isNameEditorVisible: false,
        });
    };

    renderCharacterName = (): React.ReactNode => {
        const { isReadonly, name, deathCause } = this.props;
        const { isNameEditorVisible } = this.state;

        return (
            <div
                className="ct-character-manage-pane__character-name"
                onClick={isReadonly ? undefined : this.handleNameClick}
            >
                {isNameEditorVisible && !isReadonly ? (
                    <input ref={this.editorRef} type="text" defaultValue={name} onBlur={this.handleNameChange} />
                ) : (
                    <React.Fragment>
                        <CharacterName name={name} isDead={deathCause !== Constants.DeathCauseEnum.NONE} />
                        {!isReadonly && <LightPencilSvg />}
                    </React.Fragment>
                )}
            </div>
        );
    };

    renderPlayerName = (): React.ReactNode => {
        const { userId, playerName, playerId } = this.props;

        return playerId !== userId ? (
            <div className="ct-character-manage-pane__player-name">
                <span className="ct-character-manage-pane__player-name-label">Player: </span>
                {playerName}
            </div>
        ) : null;
    };

    render() {
        const { decorationInfo, gender, xpInfo, race, preferences, ruleData, isReadonly } = this.props;

        let introClasses: Array<string> = ['ct-character-manage-pane__intro'];

        if (DecorationUtils.isDarkMode(decorationInfo)) {
            introClasses.push('ct-character-manage-pane__intro--is-dark-mode');
        }

        return (
            <div className="ct-character-manage-pane">
                <div className={introClasses.join(' ')}>
                    <div className="ct-character-manage-pane__summary">
                        <CharacterAvatarPortrait
                            className={'ct-character-manage-pane__summary-avatar'}
                            avatarUrl={DecorationUtils.getAvatarInfo(decorationInfo).avatarUrl}
                        />
                        {this.renderCharacterName()}
                        {this.renderPlayerName()}
                        <div className="ct-character-manage-pane__summary-description">
                            <CharacterSummary classes={null} race={race} gender={gender} />
                            <CharacterProgressionSummary
                                ruleData={ruleData}
                                progressionType={preferences.progressionType}
                                xpInfo={xpInfo}
                            />
                        </div>
                    </div>
                    <div className="ct-character-manage-pane__class-list">{this.renderClassList()}</div>

                    {!isReadonly && (
                        <div className="ct-character-manage-pane__feature-callout">
                            {/*TODO this classname "ct-character-header-desktop__button" needs to be a common button style.. its used alot now*/}
                            <div
                                className="ct-character-header-desktop__button ct-character-manage-pane__decorate-button"
                                onClick={this.handleDecorateMenuClick}
                            >
                                <div className="ct-character-header-desktop__button-icon">
                                    <ThemedPaintBrushSvg theme={DecorationUtils.getCharacterTheme(decorationInfo)} />
                                </div>
                                <span className="ct-character-header-desktop__button-label">
                                    Change Sheet Appearance
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                {isReadonly ? this.renderReadonlyCta() : this.renderMenu()}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        builderUrl: sheetAppSelectors.getBuilderUrl(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        name: rulesEngineSelectors.getName(state),
        gender: rulesEngineSelectors.getGender(state),
        race: rulesEngineSelectors.getRace(state),
        classes: rulesEngineSelectors.getClasses(state),
        xpInfo: rulesEngineSelectors.getExperienceInfo(state),
        deathCause: rulesEngineSelectors.getDeathCause(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        diceEnabled: appEnvSelectors.getDiceEnabled(state),
        diceFeatureConfiguration: appEnvSelectors.getDiceFeatureConfiguration(state),
        preferences: rulesEngineSelectors.getCharacterPreferences(state),
        decorationInfo: rulesEngineSelectors.getDecorationInfo(state),
        userId: appEnvSelectors.getUserId(state),
        playerName: rulesEngineSelectors.getUsername(state),
        playerId: rulesEngineSelectors.getUserId(state),
    };
}
export default PaneUtils.withPaneProperties(connect(mapStateToProps)(CharacterManagePane), {
    themeMode: ThemeModeEnum.DARK,
    isFullWidth: true,
});
